import { normalizeCogoApplicationBundle } from '@/features/project/lib/cogo-application-io'
import { deleteProject, importProjectBundle, loadProject } from '@/services/project-api'
import { useStudioStore } from '@/store/studio-store'

export interface ImportedCogoXyzApp {
  projectId: string
  name: string
  previewUrl: string
  bundleRef: string
  metadata?: Record<string, unknown>
}

function previewOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return 'https://cogo.xyz'
}

function parseCogoJsonInput(input: string): unknown {
  try {
    return JSON.parse(input) as unknown
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err)
    throw new Error(`Invalid cogo_json input: ${detail}`)
  }
}

export async function importCogoJsonAppInput(params: {
  title: string
  input: string
  preserveOpenPencilAuthority?: boolean
}): Promise<ImportedCogoXyzApp> {
  const sourceName = `${params.title}.cogo-app.json`
  const bundle = parseCogoJsonInput(params.input)
  const imported = normalizeCogoApplicationBundle(bundle, sourceName)
  await useStudioStore.getState().importCogoApplication(bundle, sourceName, {
    preserveOpenPencilAuthority:
      params.preserveOpenPencilAuthority === true,
  })
  return {
    projectId: imported.id,
    name: imported.name,
    previewUrl: `${previewOrigin()}/ide/${imported.id}`,
    bundleRef: `cogo-xyz://imported-cogo-apps/${imported.id}`,
  }
}

/**
 * Import an exact Coogo application bundle into the authenticated DGX project
 * store, then open and independently read it back before returning success.
 *
 * The older browser import above intentionally remains available for source
 * candidates whose authority is still session-scoped. Chat and Bot use this
 * durable path so a generated/imported application survives Library reloads
 * and can be edited by the same Studio project tools.
 */
export async function importPersistedCogoJsonAppInput(params: {
  title: string
  input: string
  description?: string
}): Promise<ImportedCogoXyzApp> {
  const bundle = parseCogoJsonInput(params.input)
  const imported = await importProjectBundle(bundle, {
    name: params.title,
    description:
      params.description ??
      'Imported by Studio Chat or Bot from an exact Coogo application bundle.',
  })
  try {
    const readback = await loadProject(imported.projectId)
    const readbackId = readback.project?.id
    const readbackBlockCount = Object.keys(readback.blockIdMap).length
    const readbackManifest =
      readback.app.manifest !== null &&
      typeof readback.app.manifest === 'object' &&
      !Array.isArray(readback.app.manifest)
        ? (readback.app.manifest as unknown as Record<string, unknown>)
        : {}
    const readbackPageCount = Array.isArray(readbackManifest.pages)
      ? readbackManifest.pages.length
      : 0

    if (readbackId !== imported.projectId) {
      throw new Error('Persisted Coogo application readback returned a different project')
    }
    if (readbackBlockCount !== imported.summary.blocks) {
      throw new Error(
        `Persisted Coogo application readback block count mismatch: expected ${imported.summary.blocks}, received ${readbackBlockCount}`,
      )
    }
    if (readbackPageCount !== imported.summary.pages) {
      throw new Error(
        `Persisted Coogo application readback page count mismatch: expected ${imported.summary.pages}, received ${readbackPageCount}`,
      )
    }

    await useStudioStore.getState().openServerProject(imported.projectId)
    const active = useStudioStore.getState()
    if (
      active.currentProjectId !== imported.projectId ||
      active.projectSource !== 'server'
    ) {
      throw new Error(
        'Persisted Coogo application could not be opened as the active server project',
      )
    }
  } catch (error) {
    const removed = await deleteProject(imported.projectId)
    if (!removed) {
      throw new AggregateError(
        [error],
        `Persisted Coogo application import failed and project ${imported.projectId} could not be removed`,
      )
    }
    throw error
  }

  return {
    projectId: imported.projectId,
    name: imported.name,
    previewUrl: `${previewOrigin()}/ide/${imported.projectId}`,
    bundleRef: `cogo-studio://projects/${imported.projectId}`,
    metadata: {
      persistence: 'server',
      readback: 'verified',
      sourceKind: imported.sourceKind,
      ...imported.summary,
    },
  }
}
